/**
 * Shown when the agreement has changed since the user last accepted.
 *
 * Blocking on purpose. The backend refuses the data endpoints in this state
 * anyway (`agreement_required`), so a dismissible banner would just produce an
 * app that looks broken. Sign out stays available — nobody should be trapped
 * between accepting new terms and leaving.
 */

import { useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import type { LegalDocumentSummary, LegalStatus } from "../api/types";
import { Link } from "../router";
import { LEGAL_NAV } from "./LegalPage";

interface Props {
  legal: LegalStatus;
  onAccepted: () => void;
  onSignOut: () => void;
}

export function AgreementGate({ legal, onAccepted, onSignOut }: Props) {
  const [documents, setDocuments] = useState<LegalDocumentSummary[]>([]);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    api
      .legalIndex(controller.signal)
      .then((index) => setDocuments(index.documents))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  async function accept() {
    setError("");
    setBusy(true);
    try {
      await api.acceptAgreement(legal.current_version);
      onAccepted();
    } catch (cause) {
      // A 409 means the version moved again while this was open. Reloading
      // fetches the current one rather than leaving a dead button.
      setError(
        cause instanceof ApiError
          ? `${cause.message}${cause.code === "agreement_stale" ? "" : ""}`
          : "Couldn't record your acceptance.",
      );
    } finally {
      setBusy(false);
    }
  }

  const returning = legal.accepted_version !== null;

  return (
    <div className="gate">
      <div className="card wide">
        <div className="eyebrow">Version {legal.current_version}</div>
        <h1>
          {returning ? "Our terms have changed" : "Before you continue"}
        </h1>

        <p className="lede">
          {returning
            ? "We've made a material change to the agreement. Please read it and accept the new version to carry on using Distress Signal."
            : "This account predates our recorded agreement. Please read and accept it to continue."}
        </p>

        {error && (
          <div className="err" role="alert">
            {error}
          </div>
        )}

        <ul className="doc-list">
          {(documents.length
            ? documents
            : LEGAL_NAV.map((n) => ({
                slug: n.path.slice(1),
                title: n.label,
                summary: "",
              }))
          ).map((document) => (
            <li key={document.slug}>
              <Link to={`/${document.slug}`} newTab>
                {document.title}
              </Link>
              {"summary" in document && document.summary && (
                <span className="sub"> — {document.summary}</span>
              )}
            </li>
          ))}
        </ul>

        <label className="check accept">
          <input
            type="checkbox"
            checked={checked}
            onChange={(event) => setChecked(event.target.checked)}
          />
          <span>
            I have read and accept the Terms of Service, Privacy Policy,
            Acceptable Use Policy and Data Licence.
          </span>
        </label>

        <button className="btn" disabled={!checked || busy} onClick={accept}>
          {busy ? "Recording…" : "Accept and continue"}
        </button>

        <p className="switch">
          Not ready?{" "}
          <button type="button" className="linkbtn" onClick={onSignOut}>
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}
